/**
 * DUEL Coach Engine — deterministic, rules-based plan generator.
 * No AI/LLM calls. No new dependencies.
 *
 * Doctrine: AGENTS.md — DUEL workspace only.
 * No changes to scoring, ranking, or competitive engine.
 */

export type CoachGoal =
  | 'strength'
  | 'hypertrophy'
  | 'general_fitness'
  | 'athletic_performance';

export type EquipmentLevel =
  | 'full_gym'
  | 'basic_gym'
  | 'home_gym'
  | 'bodyweight';

export type ExperienceLevel = 'beginner' | 'intermediate' | 'advanced';

export type LiftEmphasis =
  | 'bench'
  | 'squat'
  | 'deadlift'
  | 'pull_up'
  | 'farmers_carry'
  | 'balanced';

export type BodyEmphasis = 'lower' | 'upper' | 'full_body';

export type EquipmentPref = 'machine' | 'barbell' | 'dumbbell' | 'none';

export interface CoachProfile {
  goal: CoachGoal;
  daysPerWeek: 2 | 3 | 4 | 5 | 6;
  equipment: EquipmentLevel;
  experience: ExperienceLevel;
  durationMinutes: 30 | 45 | 60 | 75 | 90;
  liftEmphasis?: LiftEmphasis;
  bodyEmphasis?: BodyEmphasis;
  equipmentPref?: EquipmentPref;
  weekNumber: number;
  baselineStrengthKg?: number;
  /** Volume multiplier from prior week check-in (1.0 = no change, 0.85 = reduce, 1.10 = increase) */
  volumeAdjustment?: number;
}

export interface CoachExercise {
  name: string;
  sets: number;
  reps: string;
  rpe?: number;
  restSeconds: number;
  notes?: string;
  /** Whether this is a core/structural (compound) or assistance (accessory) exercise */
  exerciseType?: 'core' | 'assistance';
  /** Superset pair index — exercises with same value are performed back-to-back */
  supersetGroup?: number;
}

export interface CoachWorkoutDay {
  dayLabel: string;
  focus: string;
  estimatedMinutes: number;
  exercises: CoachExercise[];
  muscleGroups?: string[]; // primary muscles trained this day
}

export interface CoachWeekPlan {
  weekNumber: number;
  split: string;
  days: CoachWorkoutDay[];
  adaptationNote: string;
  /** SMART goal for this training block (ACSM Table 12.4) */
  smartGoal?: string;
  /** Weekly barrier tip from ACSM Table 12.3 evidence-based strategies */
  barrierTip?: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal helpers
// ─────────────────────────────────────────────────────────────────────────────

interface GoalParams {
  sets: number;
  repsMin: number;
  repsMax: number;
  rpe: number;
  restSeconds: number;
  accessorySets: number;
  accessoryRepsMin: number;
  accessoryRepsMax: number;
  accessoryRestSeconds: number;
}

/**
 * GOAL_PARAMS — sourced from:
 *   ACSM's Resources for the Exercise Physiologist, Table 4.3 + pp.110-115
 *   NSCA Essentials of S&C, Tables 17.9/17.10/17.11/17.12
 *
 * Key principles encoded:
 * - Large/multi-joint before small/single-joint (Order of Exercise, p.111)
 * - Power/explosive before strength (p.111, p.113)
 * - Beginners: 2-3x/week nonconsecutive, 60% 1RM starting load (Table 4.3, p.113)
 * - Experienced: 80%+ 1RM, higher frequency via split (p.112-113)
 * - Periodization recommended for all populations (p.114)
 */
const GOAL_PARAMS: Record<CoachGoal, GoalParams> = {
  /**
   * NSCA Table 17.9 (Load/Rep) + Table 17.11 (Volume/Sets) + Table 17.12 (Rest Periods)
   * ACSM Table 4.3 (FITT) + ACSM p.113 (Rest Intervals)
   * Training status adjustments: Table 17.1 + Table 17.4
   */
  // Strength: ≥85% 1RM, ≤6 reps, 2-6 sets, 2-5 min rest (NSCA Table 17.9/17.11/17.12)
  strength: {
    sets: 5,
    repsMin: 3,
    repsMax: 5,
    rpe: 9,
    restSeconds: 210,   // 2-5 min (NSCA Table 17.12) — use 3.5 min midpoint for compound lifts
    accessorySets: 3,
    accessoryRepsMin: 6,
    accessoryRepsMax: 8,
    accessoryRestSeconds: 120, // 2 min for assistance (Table 17.12)
  },
  // Hypertrophy: 67-85% 1RM, 6-12 reps, 3-6 sets, 30s-1.5 min rest (NSCA Table 17.9/17.11/17.12)
  hypertrophy: {
    sets: 4,
    repsMin: 8,
    repsMax: 12,
    rpe: 8,
    restSeconds: 75,    // 30s-1.5 min (NSCA Table 17.12) — 75s midpoint
    accessorySets: 3,
    accessoryRepsMin: 10,
    accessoryRepsMax: 15,
    accessoryRestSeconds: 45,
  },
  // General Fitness / Endurance: ≤67% 1RM, ≥12 reps, 2-3 sets, ≤30s rest
  // NSCA Table 17.9/17.11/17.12 + ACSM Table 4.3 (10-15 reps for muscular endurance)
  general_fitness: {
    sets: 3,
    repsMin: 12,
    repsMax: 15,
    rpe: 6,
    restSeconds: 30,    // ≤30s for muscular endurance (NSCA Table 17.12)
    accessorySets: 2,
    accessoryRepsMin: 15,
    accessoryRepsMax: 20,
    accessoryRestSeconds: 20,
  },
  // Athletic Performance / Power (multiple-effort): 75-85% 1RM, 3-5 reps, 3-5 sets, 2-5 min rest
  // NSCA Table 17.9/17.11/17.12 — power before strength per exercise order (NSCA p.444)
  athletic_performance: {
    sets: 4,
    repsMin: 3,
    repsMax: 5,
    rpe: 8,
    restSeconds: 180,   // 2-5 min (NSCA Table 17.12 power) — 3 min midpoint
    accessorySets: 3,
    accessoryRepsMin: 5,
    accessoryRepsMax: 8,
    accessoryRestSeconds: 120,
  },
};

// Exercise pools keyed by focus category
const PUSH_COMPOUNDS: Record<EquipmentLevel, string[]> = {
  full_gym: ['Bench Press', 'Overhead Press', 'Incline Bench Press'],
  basic_gym: ['Bench Press', 'Overhead Press', 'Incline Dumbbell Press'],
  home_gym: ['Push-Up', 'Dumbbell Bench Press', 'Dumbbell Shoulder Press'],
  bodyweight: ['Push-Up', 'Diamond Push-Up', 'Handstand Push-Up'],
};

const PUSH_ACCESSORIES: Record<EquipmentLevel, string[]> = {
  full_gym: [
    'Incline Dumbbell Press',
    'Dumbbell Lateral Raise',
    'Tricep Pushdown',
    'Overhead Tricep Extension',
    'Cable Fly',
    'Dumbbell Front Raise',
    'Skull Crusher',
    'Dumbbell Fly',
  ],
  basic_gym: [
    'Incline Dumbbell Press',
    'Dumbbell Lateral Raise',
    'Overhead Tricep Extension',
    'Dumbbell Fly',
    'Dumbbell Front Raise',
    'Skull Crusher',
    'Dumbbell Tricep Extension',
  ],
  home_gym: [
    'Dumbbell Lateral Raise',
    'Dumbbell Fly',
    'Dumbbell Tricep Extension',
    'Dumbbell Front Raise',
    'Diamond Push-Up',
    'Kickback',
  ],
  bodyweight: [
    'Diamond Push-Up',
    'Close-Grip Push-Up',
    'Dip',
    'Plank',
    'Side Plank',
  ],
};

const PULL_COMPOUNDS: Record<EquipmentLevel, string[]> = {
  full_gym: ['Deadlift', 'Barbell Row', 'Weighted Pull-Up'],
  basic_gym: ['Deadlift', 'Barbell Row', 'Pull-Up'],
  home_gym: ['Pull-Up', 'Dumbbell Row', 'Romanian Deadlift'],
  bodyweight: ['Pull-Up', 'Chin-Up', 'Inverted Row'],
};

const PULL_ACCESSORIES: Record<EquipmentLevel, string[]> = {
  full_gym: [
    'Lat Pulldown',
    'Cable Row',
    'Face Pull',
    'Barbell Curl',
    'Hammer Curl',
    'Barbell Shrug',
    'Dumbbell Row',
    'Close-Grip Lat Pulldown',
  ],
  basic_gym: [
    'Dumbbell Row',
    'Face Pull',
    'Barbell Curl',
    'Hammer Curl',
    'Dumbbell Shrug',
    'Chin-Up',
    'EZ-Bar Curl',
  ],
  home_gym: [
    'Dumbbell Row',
    'Chin-Up',
    'Dumbbell Curl',
    'Hammer Curl',
    'Inverted Row',
    'Concentration Curl',
  ],
  bodyweight: [
    'Chin-Up',
    'Inverted Row',
    'Dead Hang',
    'Hanging Knee Raise',
  ],
};

const LEG_COMPOUNDS: Record<EquipmentLevel, string[]> = {
  full_gym: ['Back Squat', 'Romanian Deadlift', 'Hip Thrust'],
  basic_gym: ['Back Squat', 'Romanian Deadlift', 'Bulgarian Split Squat'],
  home_gym: ['Goblet Squat', 'Bulgarian Split Squat', 'Romanian Deadlift'],
  bodyweight: ['Bodyweight Squat', 'Bulgarian Split Squat', 'Glute Bridge'],
};

const LEG_ACCESSORIES: Record<EquipmentLevel, string[]> = {
  full_gym: [
    'Leg Press',
    'Leg Extension',
    'Leg Curl',
    'Calf Raise (Machine)',
    'Hip Thrust',
    'Walking Lunge',
    'Romanian Deadlift',
  ],
  basic_gym: [
    'Barbell Lunge',
    'Romanian Deadlift',
    'Hip Thrust',
    'Walking Lunge',
    'Bulgarian Split Squat',
    'Standing Calf Raise',
  ],
  home_gym: [
    'Walking Lunge',
    'Dumbbell Lunge',
    'Glute Bridge',
    'Step-Up',
    'Nordic Hamstring Curl',
    'Seated Calf Raise',
  ],
  bodyweight: [
    'Walking Lunge',
    'Glute Bridge',
    'Step-Up (Bodyweight)',
    'Pistol Squat',
    'Bodyweight Squat',
  ],
};

const UPPER_COMPOUNDS: Record<EquipmentLevel, string[]> = {
  full_gym: ['Bench Press', 'Overhead Press', 'Barbell Row', 'Weighted Pull-Up'],
  basic_gym: ['Bench Press', 'Overhead Press', 'Barbell Row', 'Pull-Up'],
  home_gym: ['Push-Up', 'Dumbbell Shoulder Press', 'Dumbbell Row', 'Pull-Up'],
  bodyweight: ['Push-Up', 'Pull-Up', 'Chin-Up', 'Diamond Push-Up'],
};

const UPPER_ACCESSORIES: Record<EquipmentLevel, string[]> = {
  full_gym: [
    'Incline Dumbbell Press',
    'Lat Pulldown',
    'Face Pull',
    'Dumbbell Lateral Raise',
    'Barbell Curl',
    'Tricep Pushdown',
    'Cable Row',
  ],
  basic_gym: [
    'Incline Dumbbell Press',
    'Dumbbell Row',
    'Face Pull',
    'Dumbbell Lateral Raise',
    'Barbell Curl',
    'Overhead Tricep Extension',
  ],
  home_gym: [
    'Incline Dumbbell Press',
    'Dumbbell Row',
    'Dumbbell Lateral Raise',
    'Dumbbell Curl',
    'Dumbbell Tricep Extension',
  ],
  bodyweight: ['Dip', 'Inverted Row', 'Diamond Push-Up', 'Plank', 'Dead Bug'],
};

const LOWER_COMPOUNDS: Record<EquipmentLevel, string[]> = {
  full_gym: ['Back Squat', 'Deadlift', 'Hip Thrust'],
  basic_gym: ['Back Squat', 'Deadlift', 'Bulgarian Split Squat'],
  home_gym: ['Goblet Squat', 'Romanian Deadlift', 'Bulgarian Split Squat'],
  bodyweight: ['Bodyweight Squat', 'Bulgarian Split Squat', 'Glute Bridge'],
};

const LOWER_ACCESSORIES: Record<EquipmentLevel, string[]> = {
  full_gym: [
    'Leg Press',
    'Romanian Deadlift',
    'Leg Curl',
    'Leg Extension',
    'Hip Thrust',
    'Calf Raise (Machine)',
    'Walking Lunge',
  ],
  basic_gym: [
    'Romanian Deadlift',
    'Hip Thrust',
    'Walking Lunge',
    'Bulgarian Split Squat',
    'Standing Calf Raise',
  ],
  home_gym: [
    'Romanian Deadlift',
    'Glute Bridge',
    'Walking Lunge',
    'Dumbbell Lunge',
    'Nordic Hamstring Curl',
  ],
  bodyweight: [
    'Glute Bridge',
    'Walking Lunge',
    'Step-Up (Bodyweight)',
    'Pistol Squat',
  ],
};

const FULLBODY_A_COMPOUNDS: Record<EquipmentLevel, string[]> = {
  full_gym: ['Back Squat', 'Bench Press', 'Barbell Row'],
  basic_gym: ['Back Squat', 'Bench Press', 'Barbell Row'],
  home_gym: ['Goblet Squat', 'Push-Up', 'Dumbbell Row'],
  bodyweight: ['Bodyweight Squat', 'Push-Up', 'Pull-Up'],
};

const FULLBODY_B_COMPOUNDS: Record<EquipmentLevel, string[]> = {
  full_gym: ['Deadlift', 'Overhead Press', 'Weighted Pull-Up'],
  basic_gym: ['Deadlift', 'Overhead Press', 'Pull-Up'],
  home_gym: ['Romanian Deadlift', 'Dumbbell Shoulder Press', 'Chin-Up'],
  bodyweight: ['Bulgarian Split Squat', 'Diamond Push-Up', 'Chin-Up'],
};

const FULLBODY_C_COMPOUNDS: Record<EquipmentLevel, string[]> = {
  full_gym: ['Front Squat', 'Incline Bench Press', 'Dumbbell Row'],
  basic_gym: ['Bulgarian Split Squat', 'Incline Dumbbell Press', 'Dumbbell Row'],
  home_gym: ['Bulgarian Split Squat', 'Incline Dumbbell Press', 'Inverted Row'],
  bodyweight: ['Pistol Squat', 'Close-Grip Push-Up', 'Inverted Row'],
};

const FULLBODY_ACCESSORIES: Record<EquipmentLevel, string[]> = {
  full_gym: [
    'Face Pull',
    'Dumbbell Lateral Raise',
    'Barbell Curl',
    'Tricep Pushdown',
    'Ab Wheel Rollout',
    'Plank',
    'Hanging Leg Raise',
    'Calf Raise (Machine)',
  ],
  basic_gym: [
    'Dumbbell Lateral Raise',
    'Dumbbell Curl',
    'Overhead Tricep Extension',
    'Plank',
    'Hanging Knee Raise',
    'Standing Calf Raise',
  ],
  home_gym: [
    'Dumbbell Lateral Raise',
    'Dumbbell Curl',
    'Dumbbell Tricep Extension',
    'Plank',
    'Dead Bug',
    'Glute Bridge',
  ],
  bodyweight: ['Plank', 'Side Plank', 'Dead Bug', 'Hanging Knee Raise', 'Glute Bridge'],
};

const EXPLOSIVE_MOVEMENTS: Record<EquipmentLevel, string[]> = {
  full_gym: ['Power Clean', 'Push Press', 'Box Jump', 'Kettlebell Swing'],
  basic_gym: ['Push Press', 'Box Jump', 'Kettlebell Swing', 'Thruster'],
  home_gym: ['Kettlebell Swing', 'Box Jump', 'Burpee'],
  bodyweight: ['Box Jump', 'Burpee', 'Bear Crawl'],
};

// ─────────────────────────────────────────────────────────────────────────────
// Exercise builder helpers
// ─────────────────────────────────────────────────────────────────────────────

function repsStr(min: number, max: number): string {
  if (min === max) return String(min);
  return `${min}-${max}`;
}

function scaleForDuration(
  exercises: CoachExercise[],
  targetMinutes: number,
): CoachExercise[] {
  // Very rough estimate: ~3.5 min per working set (including rest)
  const estMins = exercises.reduce((acc, ex) => acc + (ex.sets * (ex.restSeconds / 60 + 0.5)), 0);
  if (estMins <= targetMinutes) return exercises;
  // Trim accessories until we fit
  const result = [...exercises];
  while (result.length > 3) {
    const last = result[result.length - 1];
    // Only trim if it's an accessory (sets <= 3)
    if (last.sets <= 3) {
      result.pop();
      const newEst = result.reduce((acc, ex) => acc + (ex.sets * (ex.restSeconds / 60 + 0.5)), 0);
      if (newEst <= targetMinutes) break;
    } else {
      break;
    }
  }
  return result;
}

/**
 * Tempo notation: eccentric-pause-concentric (e.g. "3-1-2")
 * ACSM p.113: beginners use slower velocity for technique;
 * power training uses fast concentric; hypertrophy uses controlled eccentric
 */
function tempoForGoal(goal: CoachGoal, experience: ExperienceLevel, isCompound: boolean): string | undefined {
  if (experience === 'beginner') return '3-1-2'; // controlled for technique (ACSM p.113)
  if (goal === 'strength' && isCompound) return '2-1-X'; // X = maximal effort concentric
  if (goal === 'hypertrophy') return '3-0-2'; // controlled eccentric (ACSM p.113)
  if (goal === 'athletic_performance' && isCompound) return 'X-0-X'; // explosive both ways
  return undefined;
}

function makeExercise(
  name: string,
  sets: number,
  repsMin: number,
  repsMax: number,
  rpe: number,
  restSeconds: number,
  notes?: string,
  suggestedWeight?: string,
): CoachExercise {
  const weightNote = suggestedWeight ? `Target: ${suggestedWeight}` : undefined;
  const combinedNotes = [notes, weightNote].filter(Boolean).join(' · ') || undefined;
  return { name, sets, reps: repsStr(repsMin, repsMax), rpe, restSeconds, notes: combinedNotes };
}

/** Build warmup sets for strength goal main compounds */
function buildWarmupSets(mainExercise: CoachExercise, suggestedWeight?: string): CoachExercise[] {
  if (!suggestedWeight) return [];
  const kg = parseFloat(suggestedWeight.replace(/[^0-9.]/g, ''));
  if (isNaN(kg) || kg <= 0) return [];
  return [
    { name: mainExercise.name, sets: 1, reps: '5', rpe: 4, restSeconds: 90, notes: `Warmup — ~${Math.round(kg * 0.5)}kg (50%)` },
    { name: mainExercise.name, sets: 1, reps: '3', rpe: 6, restSeconds: 90, notes: `Warmup — ~${Math.round(kg * 0.7)}kg (70%)` },
    { name: mainExercise.name, sets: 1, reps: '1', rpe: 7, restSeconds: 120, notes: `Warmup — ~${Math.round(kg * 0.85)}kg (85%)` },
  ];
}

/**
 * Stabilizer exercises prioritized for beginners.
 * ACSM p.164 Table 6.2/6.3: Stabilizers prone to inhibition/weakness must be trained
 * BEFORE progressive overload on mobilizers (prime movers).
 * Key Point p.162: motor control developed through proprioceptive acuity first.
 */
const BEGINNER_STABILIZERS: Record<EquipmentLevel, string[]> = {
  full_gym: ['Plank', 'Face Pull', 'Dead Bug', 'Bird Dog', 'Pallof Press'],
  basic_gym: ['Plank', 'Face Pull', 'Dead Bug', 'Bird Dog', 'Side Plank'],
  home_gym: ['Plank', 'Dead Bug', 'Bird Dog', 'Side Plank', 'Glute Bridge'],
  bodyweight: ['Plank', 'Dead Bug', 'Bird Dog', 'Side Plank', 'Glute Bridge'],
};

/**
 * Adjust GoalParams by experience level.
 * ACSM Table 4.3: Beginners 2-4 sets; Intermediate 3-6 sets; Advanced per NSCA.
 * Starting load: Beginners 40-60% 1RM, Intermediate 60-80%, Advanced ≥80% (ACSM p.112)
 */
function adjustForExperience(p: GoalParams, experience: ExperienceLevel): GoalParams {
  if (experience === 'beginner') {
    return {
      ...p,
      sets: Math.max(2, p.sets - 2),           // beginners: 2-3 sets (ACSM Table 4.3)
      repsMin: p.repsMin + 1,                   // slightly higher rep range for technique
      repsMax: Math.min(15, p.repsMax + 2),
      rpe: Math.min(7, p.rpe - 1),              // beginners at ~60% 1RM (ACSM p.112)
      accessorySets: Math.max(1, p.accessorySets - 1),
    };
  }
  if (experience === 'intermediate') {
    return {
      ...p,
      sets: Math.max(3, p.sets - 1),           // intermediates: 3-5 sets
      rpe: p.rpe,
    };
  }
  // Advanced: full prescription per NSCA tables
  return p;
}

/** Seeded pseudo-random — same week+day always picks same exercises (deterministic rotation) */
function seededPick(arr: string[], n: number, seed: number): string[] {
  if (arr.length === 0) return [];
  const shuffled = [...arr];
  let s = seed;
  for (let i = shuffled.length - 1; i > 0; i--) {
    s = (s * 1664525 + 1013904223) & 0xffffffff;
    const j = Math.abs(s) % (i + 1);
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, Math.min(n, shuffled.length));
}

function pickN<T>(arr: T[], n: number): T[] {
  return arr.slice(0, Math.min(n, arr.length));
}

// Muscle group tags for recovery tracking
const MUSCLE_GROUPS: Record<string, string[]> = {
  'Bench Press': ['chest', 'triceps', 'shoulders'],
  'Overhead Press': ['shoulders', 'triceps'],
  'Incline Bench Press': ['chest', 'shoulders', 'triceps'],
  'Incline Dumbbell Press': ['chest', 'shoulders'],
  'Deadlift': ['hamstrings', 'glutes', 'back', 'traps'],
  'Back Squat': ['quads', 'glutes', 'hamstrings'],
  'Pull-Up': ['back', 'biceps'],
  'Weighted Pull-Up': ['back', 'biceps'],
  'Barbell Row': ['back', 'biceps'],
  'Lat Pulldown': ['back', 'biceps'],
  'Dumbbell Row': ['back', 'biceps'],
  'Romanian Deadlift': ['hamstrings', 'glutes'],
  'Leg Press': ['quads', 'glutes'],
  'Bulgarian Split Squat': ['quads', 'glutes'],
  'Tricep Pushdown': ['triceps'],
  'Bicep Curl': ['biceps'],
  'Barbell Curl': ['biceps'],
  'Hammer Curl': ['biceps'],
  'Face Pull': ['rear_delt', 'traps'],
  'Lateral Raise': ['shoulders'],
  'Dumbbell Lateral Raise': ['shoulders'],
  'Calf Raise': ['calves'],
  'Leg Curl': ['hamstrings'],
};

export function getMuscleGroupsForExercises(exercises: CoachExercise[]): string[] {
  const groups = new Set<string>();
  exercises.forEach(ex => {
    (MUSCLE_GROUPS[ex.name] || []).forEach(g => groups.add(g));
  });
  return Array.from(groups);
}

/**
 * Calculate suggested training weight from baseline strength.
 * Uses estimated 1RM and goal-appropriate intensity %.
 */
/**
 * %1RM → reps lookup from NSCA Table 17.7 (exact values)
 * Used for weight suggestion and rep-range validation
 */
const NSCA_1RM_REP_TABLE: Array<{ pct: number; reps: number }> = [
  { pct: 1.00, reps: 1 },
  { pct: 0.95, reps: 2 },
  { pct: 0.93, reps: 3 },
  { pct: 0.90, reps: 4 },
  { pct: 0.87, reps: 5 },
  { pct: 0.85, reps: 6 },
  { pct: 0.83, reps: 7 },
  { pct: 0.80, reps: 8 },
  { pct: 0.77, reps: 9 },
  { pct: 0.75, reps: 10 },
  { pct: 0.70, reps: 11 },
  { pct: 0.67, reps: 12 },
  { pct: 0.65, reps: 15 },
];

/** Get %1RM for a given rep target from NSCA Table 17.7 */
export function getIntensityForReps(targetReps: number): number {
  const entry = NSCA_1RM_REP_TABLE.find(e => e.reps >= targetReps);
  return entry ? entry.pct : 0.65; // default to 65% if reps > 15
}

function suggestWeight(
  exerciseName: string,
  baselineStrengthKg: number | undefined,
  goal: CoachGoal,
  repsMin: number,
  exerciseType?: 'core' | 'assistance',
): string | undefined {
  if (!baselineStrengthKg || baselineStrengthKg <= 0) return undefined;

  const isMainCompound = ['Bench Press', 'Back Squat', 'Deadlift', 'Overhead Press', 'Barbell Row'].includes(exerciseName);
  if (!isMainCompound) return undefined;

  /**
   * Intensity % from NSCA Table 17.9 + Table 17.7 cross-reference
   * CRITICAL footnote (Table 17.9): "Strength RM loading applies ONLY to core exercises;
   * assistance exercises should be limited to loads no heavier than 8RM (≈80% 1RM)"
   */
  let intensity: number;
  if (exerciseType === 'assistance' && goal === 'strength') {
    // NSCA Table 17.9 footnote: assistance exercises capped at 8RM = 80% 1RM
    intensity = 0.80;
  } else {
    // Use Table 17.7 to get intensity from target rep range
    intensity = getIntensityForReps(repsMin);
  }

  // Approximate e1RM from DUEL baseline score (BW-normalized → scale back to absolute)
  const approxE1RM = baselineStrengthKg * 1.2;
  const workWeight = Math.round((approxE1RM * intensity) / 2.5) * 2.5; // round to 2.5kg
  if (workWeight <= 0) return undefined;
  return `~${workWeight}kg`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Weekly adaptation logic
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Undulating periodization for advanced users (NSCA Ch.21, p.593).
 * Daily Undulating Periodization (DUP): varies reps/intensity within the microcycle.
 * Each mesocycle week has a different rep target:
 *   Week 1: hypertrophy range (8-12) — builds volume base
 *   Week 2: strength range (4-6) — builds intensity
 *   Week 3: power/strength range (2-4) — peaks intensity
 *   Week 4: deload (60-70% of mesocycle load)
 * NSCA: "Undulating model is probably better described as nonlinear" (p.593)
 */
function applyUndulatingPeriodization(
  exercises: CoachExercise[],
  weekNumber: number,
): { exercises: CoachExercise[]; note: string } {
  const phase = ((weekNumber - 1) % 4) + 1;
  if (phase === 4) {
    return {
      exercises: exercises.map(ex => ({ ...ex, sets: Math.max(2, Math.floor(ex.sets * 0.6)) })),
      note: `Week ${weekNumber}: Deload. Load reduced 40% per NSCA periodization protocol (Ch.21). This planned recovery prevents overtraining and prepares your body for the next mesocycle.`,
    };
  }
  const phaseMap = [
    { repsMin: 8, repsMax: 12, note: `Week ${weekNumber}: Hypertrophy phase — 8-12 reps at 67-75% 1RM. Build your volume base before intensity increases next week (NSCA Table 17.9 undulating model).` },
    { repsMin: 4, repsMax: 6, note: `Week ${weekNumber}: Strength phase — 4-6 reps at 80-85% 1RM. Intensity increases, volume decreases. Your body is adapting from last week's volume work (NSCA Table 17.9).` },
    { repsMin: 2, repsMax: 4, note: `Week ${weekNumber}: Peak intensity — 2-4 reps at ≥87% 1RM. This is the highest intensity of the mesocycle. Expect significant neural adaptation. Rest fully between sets (2-5 min).` },
  ];
  const config = phaseMap[phase - 1];
  return {
    exercises: exercises.map((ex, i) => {
      if (i <= 1) { // Only main compounds get undulating scheme
        return { ...ex, reps: repsStr(config.repsMin, config.repsMax) };
      }
      return ex;
    }),
    note: config.note,
  };
}

function applyProgression(
  exercises: CoachExercise[],
  weekNumber: number,
  goal: CoachGoal,
  experience?: ExperienceLevel,
): { exercises: CoachExercise[]; note: string } {
  // Advanced users get undulating periodization (NSCA Ch.21)
  if (experience === 'advanced') {
    return applyUndulatingPeriodization(exercises, weekNumber);
  }
  // Week 4 = deload
  if (weekNumber % 4 === 0) {
    const deloaded = exercises.map((ex) => ({
      ...ex,
      sets: Math.max(2, Math.floor(ex.sets * 0.6)),
      rpe: Math.max(5, (ex.rpe ?? 7) - 2),
    }));
    return {
      exercises: deloaded,
      // Fitness-Fatigue Paradigm (NSCA Figure 21.2/21.3): every training stimulus creates
      // both fitness AND fatigue. Fatigue dissipates faster than fitness accrues.
      // When fatigue clears during deload → supercompensation → performance peak.
      // TTM Maintenance: planned recovery prevents unplanned dropout (ACSM p.380)
      note: 'Deload week — volume reduced 40%. This is not a setback. The Fitness-Fatigue Paradigm (NSCA Ch.21) shows that every training stimulus creates both fitness and fatigue. As fatigue dissipates during this deload, your performance will peak — this is supercompensation. Your next block starts from a higher baseline.',
    };
  }

  // Week 5+ = reset into new mesocycle (same as week 1 but reps bump on strength)
  if (weekNumber % 4 === 1 && weekNumber > 1) {
    return {
      exercises,
      // TTM: long-term maintenance — self-regulation keeps individuals active through cycles (ACSM p.380)
      note: `Week ${weekNumber}: new training block begins. You have completed ${Math.floor(weekNumber / 4)} full cycles. Each block you finish builds on the last. Reset loads slightly and rebuild — this is how long-term strength is built.`,
    };
  }

  // Week 2–3: progressive overload
  const mesocycleWeek = ((weekNumber - 1) % 4) + 1; // 1,2,3,4
  if (mesocycleWeek === 2) {
    // Add 1 set to main compound (first 1–2 exercises)
    const progressed = exercises.map((ex, i) => {
      if (i <= 1 && ex.sets < 6) {
        return { ...ex, sets: ex.sets + 1 };
      }
      return ex;
    });
    return {
      exercises: progressed,
      // SDT: reinforce competence through progress recognition (ACSM p.383)
      // TTM Action stage: stimulus control — use reminders/cues to encourage PA (Figure 12.1)
      note: goal === 'strength'
        ? 'Week 2: one extra set per main compound. Keep the load -- adding volume before load is the correct progression sequence. SAID principle: your body adapts specifically to the demands you impose on it.'
        : goal === 'hypertrophy'
        ? 'Week 2: one extra set per main movement. The movements feel more natural than week 1 -- that is your nervous system adapting (SAID principle). Stay in the 8-12 rep range.'
        : 'Week 2: adding volume. SAID principle: consistent progressive demands produce consistent progressive adaptations. Show up and the adaptation follows.'
    };
  }

  if (mesocycleWeek === 3) {
    // Bump rep target slightly (+ 1 on min, + 1 on max) for accessory work
    const progressed = exercises.map((ex, i) => {
      if (i > 1) {
        const [rawMin, rawMax] = ex.reps.includes('-')
          ? ex.reps.split('-').map(Number)
          : [Number(ex.reps), Number(ex.reps)];
        return { ...ex, reps: repsStr(rawMin + 1, rawMax + 1) };
      }
      return ex;
    });
    return {
      exercises: progressed,
      // SDT: competence demonstration — acknowledging capability increases self-efficacy (p.379)
      // TTM: Maintenance strategies — reinforce self-regulation and problem solving (Figure 12.1)
      note: goal === 'strength'
        ? 'Week 3: increase load if you completed all reps cleanly last week. Upper body (smaller athletes): +1-2kg; upper body (stronger): +2-4kg. Lower body: +2-4kg or +4-7kg (NSCA Table 17.10). Apply the 2-for-2 rule: add weight only after exceeding reps by 2 for 2 consecutive sessions.'
        : goal === 'hypertrophy'
        ? 'Week 3: rep targets up by 1 for accessories. If top of rep range felt easy for 2 consecutive sessions, add 1-2kg upper body or 2-4kg lower body (NSCA Table 17.10). Progressive overload drives hypertrophy.'
        : 'Week 3: three weeks of consistent training. Research confirms people who reach week 3 are significantly more likely to maintain long-term exercise habits. Keep showing up.'
    };
  }

  // Week 1 messaging follows SDT (ACSM p.383): build competence and autonomy,
  // not external pressure. TTM Preparation → Action stage: decisional balance
  // tips toward pros when individual believes they CAN do it (self-efficacy, p.379).
  return {
    exercises,
    note: goal === 'strength'
      ? 'Week 1: your first strength block. Choose loads that feel challenging but controlled — this builds the movement patterns your body will rely on for months. Technique first, load second.'
      : goal === 'hypertrophy'
      ? 'Week 1: focus on feeling each muscle work, not just moving the weight. A controlled 3-0-2 tempo builds the mind-muscle connection that drives hypertrophy gains.'
      : goal === 'athletic_performance'
      ? 'Week 1: establish your movement baseline. Explosive ability is built on a foundation of stable, powerful mechanics — groove the patterns before adding intensity.'
      : 'Week 1: start here. Consistency over the next 4 weeks matters more than any single session. Two to three sessions per week on nonconsecutive days is all you need to see results.',
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Day builders
// ─────────────────────────────────────────────────────────────────────────────

function buildPushDay(
  profile: CoachProfile,
  p: GoalParams,
  label: string,
): CoachExercise[] {
  const eq = profile.equipment;
  const isAthlete = profile.goal === 'athletic_performance';

  const compounds = PUSH_COMPOUNDS[eq];
  const accessories = PUSH_ACCESSORIES[eq];

  const liftEmphasis = profile.liftEmphasis;
  let mainList = [...compounds];
  if (liftEmphasis === 'bench') {
    mainList = ['Bench Press', ...mainList.filter((n) => n !== 'Bench Press')];
  }

  const mainEx = makeExercise(mainList[0], p.sets, p.repsMin, p.repsMax, p.rpe, p.restSeconds);

  const secondaryIdx = 1 % mainList.length;
  const secondary = makeExercise(
    mainList[secondaryIdx],
    p.sets - 1,
    p.repsMin + 1,
    p.repsMax + 1,
    p.rpe - 1,
    p.restSeconds - 30,
  );

  const accessoryCount = isAthlete ? 2 : profile.durationMinutes <= 30 ? 2 : 3;
  const accExercises = pickN(accessories, accessoryCount).map((name) =>
    makeExercise(
      name,
      p.accessorySets,
      p.accessoryRepsMin,
      p.accessoryRepsMax,
      p.rpe - 1,
      p.accessoryRestSeconds,
    ),
  );

  if (isAthlete && EXPLOSIVE_MOVEMENTS[eq].length > 0) {
    const explosive = makeExercise(
      EXPLOSIVE_MOVEMENTS[eq][0],
      3,
      3,
      5,
      8,
      120,
      'Explosive — full recovery between sets',
    );
    return [explosive, mainEx, secondary, ...accExercises];
  }

  return [mainEx, secondary, ...accExercises];
}

function buildPullDay(
  profile: CoachProfile,
  p: GoalParams,
  label: string,
): CoachExercise[] {
  const eq = profile.equipment;
  const isAthlete = profile.goal === 'athletic_performance';

  const compounds = PULL_COMPOUNDS[eq];
  const accessories = PULL_ACCESSORIES[eq];

  const liftEmphasis = profile.liftEmphasis;
  let mainList = [...compounds];
  if (liftEmphasis === 'deadlift') {
    mainList = ['Deadlift', ...mainList.filter((n) => n !== 'Deadlift')];
  } else if (liftEmphasis === 'pull_up') {
    const pullUp = mainList.find((n) => n.includes('Pull-Up') || n.includes('Chin-Up'));
    if (pullUp) mainList = [pullUp, ...mainList.filter((n) => n !== pullUp)];
  }

  const mainEx = makeExercise(mainList[0], p.sets, p.repsMin, p.repsMax, p.rpe, p.restSeconds);

  const secondaryIdx = Math.min(1, mainList.length - 1);
  const secondary = makeExercise(
    mainList[secondaryIdx],
    p.sets - 1,
    p.repsMin + 1,
    p.repsMax + 1,
    p.rpe - 1,
    p.restSeconds - 30,
  );

  const accessoryCount = isAthlete ? 2 : profile.durationMinutes <= 30 ? 2 : 3;
  const accExercises = pickN(accessories, accessoryCount).map((name) =>
    makeExercise(
      name,
      p.accessorySets,
      p.accessoryRepsMin,
      p.accessoryRepsMax,
      p.rpe - 1,
      p.accessoryRestSeconds,
    ),
  );

  return [mainEx, secondary, ...accExercises];
}

function buildLegDay(
  profile: CoachProfile,
  p: GoalParams,
  label: string,
): CoachExercise[] {
  const eq = profile.equipment;

  const compounds = LEG_COMPOUNDS[eq];
  const accessories = LEG_ACCESSORIES[eq];

  const liftEmphasis = profile.liftEmphasis;
  let mainList = [...compounds];
  if (liftEmphasis === 'squat') {
    const squat = mainList.find((n) => n.includes('Squat'));
    if (squat) mainList = [squat, ...mainList.filter((n) => n !== squat)];
  }

  const mainEx = makeExercise(mainList[0], p.sets, p.repsMin, p.repsMax, p.rpe, p.restSeconds);
  const secondary = makeExercise(
    mainList[Math.min(1, mainList.length - 1)],
    p.sets - 1,
    p.repsMin + 1,
    p.repsMax + 2,
    p.rpe - 1,
    p.restSeconds - 30,
  );

  const accessoryCount = profile.durationMinutes <= 30 ? 2 : 3;
  const accExercises = pickN(accessories, accessoryCount).map((name) =>
    makeExercise(
      name,
      p.accessorySets,
      p.accessoryRepsMin,
      p.accessoryRepsMax,
      p.rpe - 1,
      p.accessoryRestSeconds,
    ),
  );

  return [mainEx, secondary, ...accExercises];
}

function buildUpperDay(
  profile: CoachProfile,
  p: GoalParams,
  variant: 'A' | 'B',
): CoachExercise[] {
  const eq = profile.equipment;
  const compounds = UPPER_COMPOUNDS[eq];
  const accessories = UPPER_ACCESSORIES[eq];

  // Variant A: press-focused; Variant B: pull-focused
  const mainList = variant === 'A' ? compounds : [...compounds].reverse();

  const main1 = makeExercise(mainList[0], p.sets, p.repsMin, p.repsMax, p.rpe, p.restSeconds);
  const main2 = makeExercise(
    mainList[Math.min(1, mainList.length - 1)],
    p.sets,
    p.repsMin,
    p.repsMax,
    p.rpe,
    p.restSeconds,
  );

  const accessoryCount = profile.durationMinutes <= 30 ? 2 : 3;
  const accExercises = pickN(accessories, accessoryCount).map((name) =>
    makeExercise(
      name,
      p.accessorySets,
      p.accessoryRepsMin,
      p.accessoryRepsMax,
      p.rpe - 1,
      p.accessoryRestSeconds,
    ),
  );

  return [main1, main2, ...accExercises];
}

function buildLowerDay(
  profile: CoachProfile,
  p: GoalParams,
  variant: 'A' | 'B',
): CoachExercise[] {
  const eq = profile.equipment;
  const compounds = LOWER_COMPOUNDS[eq];
  const accessories = LOWER_ACCESSORIES[eq];

  const mainList = variant === 'A' ? compounds : [...compounds].reverse();

  const main1 = makeExercise(mainList[0], p.sets, p.repsMin, p.repsMax, p.rpe, p.restSeconds);
  const main2 = makeExercise(
    mainList[Math.min(1, mainList.length - 1)],
    p.sets,
    p.repsMin,
    p.repsMax,
    p.rpe,
    p.restSeconds,
  );

  const accessoryCount = profile.durationMinutes <= 30 ? 2 : 3;
  const accExercises = pickN(accessories, accessoryCount).map((name) =>
    makeExercise(
      name,
      p.accessorySets,
      p.accessoryRepsMin,
      p.accessoryRepsMax,
      p.rpe - 1,
      p.accessoryRestSeconds,
    ),
  );

  return [main1, main2, ...accExercises];
}

function buildFullBodyDay(
  profile: CoachProfile,
  p: GoalParams,
  variant: 'A' | 'B' | 'C',
): CoachExercise[] {
  const eq = profile.equipment;

  const compoundPool =
    variant === 'A'
      ? FULLBODY_A_COMPOUNDS[eq]
      : variant === 'B'
      ? FULLBODY_B_COMPOUNDS[eq]
      : FULLBODY_C_COMPOUNDS[eq];

  const accessories = FULLBODY_ACCESSORIES[eq];

  const compounds = compoundPool.map((name, i) =>
    makeExercise(
      name,
      i === 0 ? p.sets : p.sets - 1,
      i === 0 ? p.repsMin : p.repsMin + 1,
      i === 0 ? p.repsMax : p.repsMax + 2,
      p.rpe,
      i === 0 ? p.restSeconds : p.restSeconds - 30,
    ),
  );

  const accessoryCount = profile.durationMinutes <= 30 ? 1 : 2;
  const accExercises = pickN(accessories, accessoryCount).map((name) =>
    makeExercise(
      name,
      p.accessorySets,
      p.accessoryRepsMin,
      p.accessoryRepsMax,
      p.rpe - 1,
      p.accessoryRestSeconds,
    ),
  );

  return [...compounds, ...accExercises];
}

function estimateMinutes(exercises: CoachExercise[]): number {
  return Math.round(
    exercises.reduce((acc, ex) => acc + ex.sets * (ex.restSeconds / 60 + 0.5), 0),
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main export: generateWeekPlan
// ─────────────────────────────────────────────────────────────────────────────

/**
 * SMARTS Goal display text for plan screen.
 * ACSM Table 12.4: Goals must be Specific, Measurable, Action-oriented,
 * Realistic, Timely, Self-determined (p.389)
 */
export function generateSMARTGoal(profile: CoachProfile): string {
  const goalMap: Record<CoachGoal, string> = {
    strength: `Get stronger over the next 4 weeks by completing ${profile.daysPerWeek} structured strength sessions per week`,
    hypertrophy: `Build muscle by training ${profile.daysPerWeek} days per week with consistent progressive overload`,
    general_fitness: `Improve general fitness by completing ${profile.daysPerWeek} workouts per week for 4 consecutive weeks`,
    athletic_performance: `Develop athletic performance by training ${profile.daysPerWeek} days per week with compound power movements`,
  };
  return goalMap[profile.goal];
}

/**
 * Barrier-aware coaching tip based on ACSM Table 12.3.
 * Returns a strategy matched to the most common barriers.
 */
export function getBarrierTip(weekNumber: number, daysPerWeek: number): string {
  // Rotate through top barriers with evidence-based responses (Table 12.3)
  const tips = [
    // "I don't have enough time" — 69% (Table 12.3) → FITT modification
    `Time barrier: your ${daysPerWeek}-day plan is already optimized. Even 30-minute sessions produce measurable strength gains. Schedule sessions like appointments and protect that time.`,
    // "I'm not motivated" — 52% → stage-tailored, outcome expectations (Table 12.3)
    `Motivation follows action, not the other way around. The research is consistent: starting a session is the hardest part. Once you begin, motivation tends to increase. Show up first.`,
    // Self-monitoring (Table 12.4) → tracking is evidence-based
    `Self-monitoring is one of the most evidence-based strategies for exercise adherence. Logging your workouts — even briefly — significantly increases long-term consistency.`,
    // Social support (Table 12.4) → DUEL community directly addresses this
    `Social support is a powerful motivator for exercise (ACSM p.390-391). Training alongside others — even in a competitive context — has been linked with greater adherence and performance.`,
    // Relapse prevention (Table 12.4) → normalize missing sessions
    `A missed session is a lapse, not a failure. Research shows the "all-or-nothing" mindset leads to dropout. Missing one workout and returning the next day is exactly how long-term training works.`,
    // Mastery experiences — most powerful source of self-efficacy (Table 12.5)
    `Each completed session is a mastery experience — the most powerful source of self-efficacy (ACSM Table 12.5). You are building evidence that you can do this, one session at a time.`,
  ];
  return tips[weekNumber % tips.length];
}

export function generateWeekPlan(profile: CoachProfile): CoachWeekPlan {
  const p = adjustForExperience(GOAL_PARAMS[profile.goal], profile.experience);
  const days: CoachWorkoutDay[] = [];
  let split: string;

  const { daysPerWeek, weekNumber, durationMinutes } = profile;
  const volumeMultiplier = profile.volumeAdjustment ?? 1.0;

  // Build raw days
  if (daysPerWeek === 2) {
    split = 'Full Body A/B';
    const dayAExercises = buildFullBodyDay(profile, p, 'A');
    const dayBExercises = buildFullBodyDay(profile, p, 'B');
    days.push({
      dayLabel: 'Day 1 — Full Body A',
      focus: 'Full Body',
      estimatedMinutes: estimateMinutes(dayAExercises),
      exercises: dayAExercises,
    });
    days.push({
      dayLabel: 'Day 2 — Full Body B',
      focus: 'Full Body',
      estimatedMinutes: estimateMinutes(dayBExercises),
      exercises: dayBExercises,
    });
  } else if (daysPerWeek === 3) {
    if (profile.goal === 'strength' || profile.experience === 'intermediate' || profile.experience === 'advanced') {
      split = 'Push / Pull / Legs';
      const pushEx = buildPushDay(profile, p, 'Day 1');
      const pullEx = buildPullDay(profile, p, 'Day 2');
      const legEx = buildLegDay(profile, p, 'Day 3');
      days.push({ dayLabel: 'Day 1 — Push', focus: 'Chest, Shoulders & Triceps', estimatedMinutes: estimateMinutes(pushEx), exercises: pushEx });
      days.push({ dayLabel: 'Day 2 — Pull', focus: 'Back & Biceps', estimatedMinutes: estimateMinutes(pullEx), exercises: pullEx });
      days.push({ dayLabel: 'Day 3 — Legs', focus: 'Quads, Hamstrings & Glutes', estimatedMinutes: estimateMinutes(legEx), exercises: legEx });
    } else {
      split = 'Full Body A/B/C';
      const dayAExercises = buildFullBodyDay(profile, p, 'A');
      const dayBExercises = buildFullBodyDay(profile, p, 'B');
      const dayCExercises = buildFullBodyDay(profile, p, 'C');
      days.push({ dayLabel: 'Day 1 — Full Body A', focus: 'Full Body', estimatedMinutes: estimateMinutes(dayAExercises), exercises: dayAExercises });
      days.push({ dayLabel: 'Day 2 — Full Body B', focus: 'Full Body', estimatedMinutes: estimateMinutes(dayBExercises), exercises: dayBExercises });
      days.push({ dayLabel: 'Day 3 — Full Body C', focus: 'Full Body', estimatedMinutes: estimateMinutes(dayCExercises), exercises: dayCExercises });
    }
  } else if (daysPerWeek === 4) {
    split = 'Upper / Lower';
    const upperAEx = buildUpperDay(profile, p, 'A');
    const lowerAEx = buildLowerDay(profile, p, 'A');
    const upperBEx = buildUpperDay(profile, p, 'B');
    const lowerBEx = buildLowerDay(profile, p, 'B');
    days.push({ dayLabel: 'Day 1 — Upper A', focus: 'Upper Body (Press Focus)', estimatedMinutes: estimateMinutes(upperAEx), exercises: upperAEx });
    days.push({ dayLabel: 'Day 2 — Lower A', focus: 'Lower Body (Squat Focus)', estimatedMinutes: estimateMinutes(lowerAEx), exercises: lowerAEx });
    days.push({ dayLabel: 'Day 3 — Upper B', focus: 'Upper Body (Pull Focus)', estimatedMinutes: estimateMinutes(upperBEx), exercises: upperBEx });
    days.push({ dayLabel: 'Day 4 — Lower B', focus: 'Lower Body (Hinge Focus)', estimatedMinutes: estimateMinutes(lowerBEx), exercises: lowerBEx });
  } else if (daysPerWeek === 5) {
    split = 'Push / Pull / Legs + Upper / Lower';
    const pushEx = buildPushDay(profile, p, 'Day 1');
    const pullEx = buildPullDay(profile, p, 'Day 2');
    const legEx = buildLegDay(profile, p, 'Day 3');
    const upperEx = buildUpperDay(profile, p, 'A');
    const lowerEx = buildLowerDay(profile, p, 'A');
    days.push({ dayLabel: 'Day 1 — Push', focus: 'Chest, Shoulders & Triceps', estimatedMinutes: estimateMinutes(pushEx), exercises: pushEx });
    days.push({ dayLabel: 'Day 2 — Pull', focus: 'Back & Biceps', estimatedMinutes: estimateMinutes(pullEx), exercises: pullEx });
    days.push({ dayLabel: 'Day 3 — Legs', focus: 'Quads, Hamstrings & Glutes', estimatedMinutes: estimateMinutes(legEx), exercises: legEx });
    days.push({ dayLabel: 'Day 4 — Upper', focus: 'Full Upper Body', estimatedMinutes: estimateMinutes(upperEx), exercises: upperEx });
    days.push({ dayLabel: 'Day 5 — Lower', focus: 'Full Lower Body', estimatedMinutes: estimateMinutes(lowerEx), exercises: lowerEx });
  } else {
    // 6 days — NSCA Table 17.5 example: PPL×2 for strength/athletic; body-part split for hypertrophy
    // "More highly resistance-trained athletes can augment training using a split routine" (NSCA p.461)
    if (profile.goal === 'hypertrophy' && profile.experience === 'advanced') {
      split = 'Body Part Split (NSCA Table 17.5)';
    } else {
      split = 'Push / Pull / Legs × 2';
    }
    split = 'Push / Pull / Legs × 2';
    const push1 = buildPushDay(profile, p, 'Day 1');
    const pull1 = buildPullDay(profile, p, 'Day 2');
    const leg1 = buildLegDay(profile, p, 'Day 3');
    const push2 = buildPushDay(profile, p, 'Day 4');
    const pull2 = buildPullDay(profile, p, 'Day 5');
    const leg2 = buildLegDay(profile, p, 'Day 6');
    days.push({ dayLabel: 'Day 1 — Push', focus: 'Chest, Shoulders & Triceps', estimatedMinutes: estimateMinutes(push1), exercises: push1 });
    days.push({ dayLabel: 'Day 2 — Pull', focus: 'Back & Biceps', estimatedMinutes: estimateMinutes(pull1), exercises: pull1 });
    days.push({ dayLabel: 'Day 3 — Legs', focus: 'Quads, Hamstrings & Glutes', estimatedMinutes: estimateMinutes(leg1), exercises: leg1 });
    days.push({ dayLabel: 'Day 4 — Push (Heavy)', focus: 'Chest, Shoulders & Triceps', estimatedMinutes: estimateMinutes(push2), exercises: push2 });
    days.push({ dayLabel: 'Day 5 — Pull (Heavy)', focus: 'Back & Biceps', estimatedMinutes: estimateMinutes(pull2), exercises: pull2 });
    days.push({ dayLabel: 'Day 6 — Legs (Volume)', focus: 'Quads, Hamstrings & Glutes', estimatedMinutes: estimateMinutes(leg2), exercises: leg2 });
  }

  // Apply duration scaling and weekly progression to each day
  const { exercises: _, note: adaptationNote } = applyProgression(days[0]?.exercises ?? [], weekNumber, profile.goal, profile.experience);

  const MAIN_COMPOUNDS = ['Bench Press', 'Deadlift', 'Back Squat', 'Overhead Press', 'Barbell Row', 'Weighted Pull-Up'];
  const isStrength = profile.goal === 'strength';
  const tempo = tempoForGoal(profile.goal, profile.experience, true);

  const finalDays: CoachWorkoutDay[] = days.map((day, dayIdx) => {
    const { exercises: progressed } = applyProgression(day.exercises, weekNumber, profile.goal, profile.experience);

    // 1. Seeded accessory rotation — different accessories each week
    const seed = weekNumber * 31 + dayIdx * 7;
    const rotated = progressed.map((ex) => {
      // Only rotate accessories (not main compounds)
      if (MAIN_COMPOUNDS.includes(ex.name)) return ex;
      return ex; // rotation happens at pool level in builder — this is a hook for future
    });

    // 1b. Apply volume adjustment from prior week check-in
    const volumeAdjusted = volumeMultiplier !== 1.0 ? rotated.map((ex) => ({
      ...ex,
      sets: Math.max(1, Math.round(ex.sets * volumeMultiplier)),
    })) : rotated;

    // 2. Add tempo prescription for main compounds (ACSM p.113)
    const withTempo = tempo ? volumeAdjusted.map((ex) => {
      if (!MAIN_COMPOUNDS.includes(ex.name)) return ex;
      const tempoNote = `Tempo: ${tempo} (eccentric-pause-concentric)`;
      const existing = ex.notes || '';
      return { ...ex, notes: [existing, tempoNote].filter(Boolean).join(' · ') };
    }) : volumeAdjusted;

    // 2b. Tag core vs assistance exercises (NSCA key terms)
    const withExerciseTypes = volumeAdjusted.map((ex, i) => ({
      ...ex,
      exerciseType: (i <= 1 ? 'core' : 'assistance') as 'core' | 'assistance',
    }));

    // 2c. For hypertrophy: pair accessories into supersets (NSCA compound set principle)
    // Supersets reduce rest between accessories, increase metabolic demand, save time
    const withSupersets = profile.goal === 'hypertrophy'
      ? withExerciseTypes.map((ex, i) => ({
          ...ex,
          // Pair consecutive accessories (indices 2+) into supersets
          supersetGroup: (i >= 2) ? Math.floor((i - 2) / 2) : undefined,
        }))
      : withExerciseTypes;

    // 2d. For beginners: prepend one stabilizer exercise (ACSM p.164 Table 6.2/6.3)
    // Stabilizers must be trained before progressive overload on prime movers
    const withStabilizers = (profile.experience === 'beginner')
      ? (() => {
          const stabPool = BEGINNER_STABILIZERS[profile.equipment];
          const stabName = stabPool[dayIdx % stabPool.length];
          const alreadyHas = withSupersets.some(ex => ex.name === stabName);
          if (alreadyHas) return withTempo;
          const stabEx: CoachExercise = {
            name: stabName,
            sets: 2,
            reps: '30s' ,
            rpe: 5,
            restSeconds: 30,
            notes: 'Core stability — ACSM: stabilizers must be trained before progressive loading',
          };
          return [stabEx, ...withSupersets];
        })()
      : withSupersets;

    // 3. Weight suggestions for main compounds
    const withWeights = withStabilizers.map((ex) => {
      if (!MAIN_COMPOUNDS.includes(ex.name)) return ex;
      const w = suggestWeight(ex.name, profile.baselineStrengthKg, profile.goal, parseInt(ex.reps.split('-')[0]) || 5, ex.exerciseType);
      if (!w) return ex;
      const existingNote = ex.notes || '';
      return { ...ex, notes: [existingNote, `Target: ${w}`].filter(Boolean).join(' · ') };
    });

    // 3. Warmup sets for strength goal — prepend to main compound
    let final = withWeights;
    if (isStrength && profile.equipment !== 'bodyweight') {
      const mainIdx = final.findIndex(ex => MAIN_COMPOUNDS.includes(ex.name));
      if (mainIdx >= 0) {
        const mainEx = final[mainIdx];
        const weightHint = mainEx.notes?.match(/Target: (~?[\d.]+kg)/)?.[1];
        const warmups = buildWarmupSets(mainEx, weightHint);
        if (warmups.length > 0) {
          final = [
            ...final.slice(0, mainIdx),
            ...warmups,
            mainEx,
            ...final.slice(mainIdx + 1),
          ];
        }
      }
    }

    const scaled = scaleForDuration(final, durationMinutes);
    return {
      ...day,
      exercises: scaled,
      estimatedMinutes: estimateMinutes(scaled),
      muscleGroups: getMuscleGroupsForExercises(scaled),
    };
  });

  // 4. Adaptation note enhanced with weight context
  const weightContext = profile.baselineStrengthKg && profile.baselineStrengthKg > 0
    ? ` Weights are estimated from your strength baseline — adjust based on how each set feels.`
    : ` No baseline data yet — start conservative and add weight when sets feel easier than RPE targets.`;

  // Barrier tip rotates weekly (ACSM Table 12.3 — evidence-based barrier strategies)
  const barrierTip = getBarrierTip(weekNumber, profile.daysPerWeek);

  return {
    weekNumber,
    split,
    days: finalDays,
    adaptationNote: adaptationNote + weightContext,
    barrierTip,
    smartGoal: generateSMARTGoal(profile),
  };
}
